/**
 * Organization Tab Content Component
 *
 * Displays different content based on org membership:
 * - If user has NO organization: Show create org form
 * - If user IN organization: Show org summary & quick links
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../config/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import CreateOrganizationModal from './organization/CreateOrganizationModal';
import { useAuth } from '../context/AuthContext';

export default function OrganizationTabContent({
  hasOrganization,
  organizationId,
  organizationRole,
  userProfile,
  showCreateOrgModal,
  setShowCreateOrgModal
}) {
  const navigate = useNavigate();
  const { userProfile: authUserProfile } = useAuth();
  const [orgData, setOrgData] = useState(null);
  const [memberCount, setMemberCount] = useState(0);
  const [loading, setLoading] = useState(hasOrganization);
  const [error, setError] = useState(null);

  // Fetch organization data
  useEffect(() => {
    if (!hasOrganization || !organizationId) {
      setLoading(false);
      return;
    }

    const fetchOrgData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch org document
        const orgRef = doc(db, 'organizations', organizationId);
        const orgSnap = await getDoc(orgRef);

        if (!orgSnap.exists()) {
          setError('Organization not found');
          setOrgData(null);
          return;
        }

        const org = orgSnap.data();
        setOrgData(org);

        // Fetch member count
        const membersCollection = collection(db, 'organizations', organizationId, 'members');
        const membersSnap = await getDocs(membersCollection);
        setMemberCount(membersSnap.size);
      } catch (err) {
        console.error('Error fetching organization:', err);
        setError('Failed to load organization data');
      } finally {
        setLoading(false);
      }
    };

    fetchOrgData();
  }, [organizationId, hasOrganization]);

  // User NOT in organization - Show create org form
  if (!hasOrganization) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12 sm:pb-20">
        <div className="space-y-6">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1d1d1f] dark:text-white mb-3">
              No Organization Yet
            </h2>
            <p className="text-lg text-[#6e6e73] dark:text-[#8e8e93]">
              Create your organisation to invite team members and collaborate
            </p>
          </div>

          {/* Create Org Card */}
          <div className="bg-gradient-to-br from-purple-50 dark:from-purple-900/20 to-purple-100 dark:to-purple-900/10 rounded-3xl p-8 sm:p-10 border-2 border-purple-200 dark:border-purple-800">
            <div className="flex flex-col sm:flex-row items-center gap-8">
              <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-purple-600 to-purple-700 rounded-3xl flex items-center justify-center flex-shrink-0 shadow-lg">
                <svg className="w-12 h-12 sm:w-16 sm:h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m0 0h6m-6-6V6m0 0H6M6 6v6m-6 0h6m0 0h6m-6-6H6" />
                </svg>
              </div>

              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-2xl font-bold text-[#1d1d1f] dark:text-white mb-3">
                  Create Your Organisation
                </h3>
                <p className="text-[#6e6e73] dark:text-[#8e8e93] mb-6 leading-relaxed">
                  Set up your organisation, invite team members, and unlock powerful collaboration features.
                </p>

                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-sm text-[#1d1d1f] dark:text-white">
                    <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Invite up to {orgData?.seats || 5}+ team members
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#1d1d1f] dark:text-white">
                    <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Access analytics & reporting
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#1d1d1f] dark:text-white">
                    <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Unified billing & seat management
                  </div>
                </div>

                <button
                  onClick={() => setShowCreateOrgModal(true)}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-full font-medium hover:shadow-lg hover:scale-105 transition-all"
                >
                  Create Organisation Now
                </button>
              </div>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
            <div className="bg-white dark:bg-[#2c2c2e] rounded-2xl p-6 border border-[#d2d2d7] dark:border-[#3c3c3e]">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3.914a3 3 0 01-2.857-4.431C2.736 15.39 4.565 14 6.514 14H12m-7-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="font-semibold text-[#1d1d1f] dark:text-white mb-2">Team Collaboration</h4>
              <p className="text-sm text-[#6e6e73] dark:text-[#8e8e93]">
                Work together seamlessly with your team members
              </p>
            </div>

            <div className="bg-white dark:bg-[#2c2c2e] rounded-2xl p-6 border border-[#d2d2d7] dark:border-[#3c3c3e]">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h4 className="font-semibold text-[#1d1d1f] dark:text-white mb-2">Analytics</h4>
              <p className="text-sm text-[#6e6e73] dark:text-[#8e8e93]">
                Track usage and insights across your team
              </p>
            </div>

            <div className="bg-white dark:bg-[#2c2c2e] rounded-2xl p-6 border border-[#d2d2d7] dark:border-[#3c3c3e]">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h4 className="font-semibold text-[#1d1d1f] dark:text-white mb-2">Premium Features</h4>
              <p className="text-sm text-[#6e6e73] dark:text-[#8e8e93]">
                Access all Team tier features and benefits
              </p>
            </div>
          </div>
        </div>

        {/* Create Org Modal */}
        <CreateOrganizationModal
          isOpen={showCreateOrgModal}
          onClose={() => setShowCreateOrgModal(false)}
          userProfile={authUserProfile}
        />
      </div>
    );
  }

  // User IN organization - Show org summary
  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12 sm:pb-20">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-purple-200 dark:border-purple-700 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#6e6e73] dark:text-[#8e8e93]">Loading organization...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !orgData) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12 sm:pb-20">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-8 text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-2">Error Loading Organisation</h3>
          <p className="text-red-700 dark:text-red-400">{error || 'Unable to load organisation data'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12 sm:pb-20">
      <div className="space-y-6">
        {/* Organization Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            {orgData.brandingLogo ? (
              <img
                src={orgData.brandingLogo}
                alt={orgData.name}
                className="w-16 h-16 rounded-2xl object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                {orgData.name[0]?.toUpperCase()}
              </div>
            )}
            <div>
              <h2 className="text-3xl font-bold text-[#1d1d1f] dark:text-white mb-1">
                {orgData.name}
              </h2>
              <p className="text-[#6e6e73] dark:text-[#8e8e93]">
                {organizationRole?.charAt(0).toUpperCase() + organizationRole?.slice(1) || 'Member'} • {orgData.slug}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/org/${orgData.slug}`)}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-full font-medium transition-all hover:shadow-lg"
          >
            Open Dashboard →
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-[#2c2c2e] rounded-2xl p-6 border border-[#d2d2d7] dark:border-[#3c3c3e]">
            <div className="text-[#6e6e73] dark:text-[#8e8e93] text-sm font-medium mb-2">Team Members</div>
            <div className="text-3xl font-bold text-[#1d1d1f] dark:text-white">{memberCount}</div>
            <div className="text-xs text-[#86868b] dark:text-[#8e8e93] mt-1">
              of {orgData.seats} seats
            </div>
          </div>

          <div className="bg-white dark:bg-[#2c2c2e] rounded-2xl p-6 border border-[#d2d2d7] dark:border-[#3c3c3e]">
            <div className="text-[#6e6e73] dark:text-[#8e8e93] text-sm font-medium mb-2">Available Seats</div>
            <div className="text-3xl font-bold text-[#1d1d1f] dark:text-white">
              {orgData.seats - memberCount}
            </div>
            <div className="text-xs text-[#86868b] dark:text-[#8e8e93] mt-1">
              ${orgData.seats * 20}/month
            </div>
          </div>

          <div className="bg-white dark:bg-[#2c2c2e] rounded-2xl p-6 border border-[#d2d2d7] dark:border-[#3c3c3e]">
            <div className="text-[#6e6e73] dark:text-[#8e8e93] text-sm font-medium mb-2">Your Role</div>
            <div className="text-2xl font-bold text-purple-600 capitalize">{organizationRole || 'Member'}</div>
            <div className="text-xs text-[#86868b] dark:text-[#8e8e93] mt-1">
              {organizationRole === 'owner' ? 'Full access' : organizationRole === 'admin' ? 'Admin access' : 'Limited access'}
            </div>
          </div>

          <div className="bg-white dark:bg-[#2c2c2e] rounded-2xl p-6 border border-[#d2d2d7] dark:border-[#3c3c3e]">
            <div className="text-[#6e6e73] dark:text-[#8e8e93] text-sm font-medium mb-2">Status</div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <div className="text-2xl font-bold text-green-600">Active</div>
            </div>
            <div className="text-xs text-[#86868b] dark:text-[#8e8e93] mt-1">
              All systems operational
            </div>
          </div>
        </div>

        {/* Seat Usage Bar */}
        <div className="bg-white dark:bg-[#2c2c2e] rounded-2xl p-6 border border-[#d2d2d7] dark:border-[#3c3c3e]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#1d1d1f] dark:text-white">Seat Usage</h3>
            <span className="text-sm text-[#6e6e73] dark:text-[#8e8e93]">
              {memberCount}/{orgData.seats} ({Math.round((memberCount / orgData.seats) * 100)}%)
            </span>
          </div>
          <div className="w-full bg-[#e8e8ed] dark:bg-[#1c1c1e] rounded-full h-3 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-600 to-purple-500 transition-all"
              style={{ width: `${(memberCount / orgData.seats) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Feature Shortcuts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate(`/org/${orgData.slug}#members`)}
            className="bg-white dark:bg-[#2c2c2e] rounded-2xl p-6 border border-[#d2d2d7] dark:border-[#3c3c3e] hover:shadow-md transition-all text-left group"
          >
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3.914a3 3 0 01-2.857-4.431C2.736 15.39 4.565 14 6.514 14H12m-7-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="font-semibold text-[#1d1d1f] dark:text-white mb-1">Members</h4>
            <p className="text-sm text-[#6e6e73] dark:text-[#8e8e93]">Manage team members and roles</p>
          </button>

          <button
            onClick={() => navigate(`/org/${orgData.slug}#analytics`)}
            className="bg-white dark:bg-[#2c2c2e] rounded-2xl p-6 border border-[#d2d2d7] dark:border-[#3c3c3e] hover:shadow-md transition-all text-left group"
          >
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-200 dark:group-hover:bg-green-900/50 transition-colors">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h4 className="font-semibold text-[#1d1d1f] dark:text-white mb-1">Analytics</h4>
            <p className="text-sm text-[#6e6e73] dark:text-[#8e8e93]">View team usage and insights</p>
          </button>

          <button
            onClick={() => navigate(`/org/${orgData.slug}#settings`)}
            className="bg-white dark:bg-[#2c2c2e] rounded-2xl p-6 border border-[#d2d2d7] dark:border-[#3c3c3e] hover:shadow-md transition-all text-left group"
          >
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-200 dark:group-hover:bg-orange-900/50 transition-colors">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h4 className="font-semibold text-[#1d1d1f] dark:text-white mb-1">Settings</h4>
            <p className="text-sm text-[#6e6e73] dark:text-[#8e8e93]">Configure your organisation</p>
          </button>
        </div>
      </div>
    </div>
  );
}
